import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  FormControlLabel,
  Checkbox,
  Alert,
  Grid,
} from '@mui/material';
import { FileDownload as DownloadIcon } from '@mui/icons-material';
import { getToken } from 'src/auth';
import { setBreadcrumbs } from 'src/ui';
import { downloadXlsxReport } from '../services/payoutsApi';

function saveBlob(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  window.URL.revokeObjectURL(url);
}

function ReportsPageComponent({ token, setBreadcrumbs }) {
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(false);

  // Bank spreadsheet filters
  const [payoutStatus, setPayoutStatus] = useState('approved');
  const [payoutStart, setPayoutStart] = useState('');
  const [payoutEnd, setPayoutEnd] = useState('');

  // Address ledger filters
  const [ledgerStart, setLedgerStart] = useState('');
  const [ledgerEnd, setLedgerEnd] = useState('');
  const [ledgerOnlyPaid, setLedgerOnlyPaid] = useState(false);

  // ISO payout spreadsheet filters
  const [isoStatus, setIsoStatus] = useState('approved');
  const [isoStart, setIsoStart] = useState('');
  const [isoEnd, setIsoEnd] = useState('');

  // Settlement reconciliation filters
  const [reconStart, setReconStart] = useState('');
  const [reconEnd, setReconEnd] = useState('');

  useEffect(() => {
    setBreadcrumbs([
      { label: 'SolventPay', href: '/solventpay' },
      { label: 'Reports', href: '/solventpay/reports' },
    ]);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const datestamp = () =>
    new Date()
      .toISOString()
      .slice(0, 10)
      .replace(/-/g, '');

  const handleDownloadBankSpreadsheet = async () => {
    try {
      setDownloading(true);
      setError(null);
      const params = new URLSearchParams();
      if (payoutStatus !== 'all') params.append('status', payoutStatus);
      if (payoutStart) params.append('start_date', payoutStart);
      if (payoutEnd) params.append('end_date', payoutEnd);
      const qs = params.toString();
      const blob = await downloadXlsxReport(
        `/api/v1/payouts/spreadsheet/merchant/${qs ? '?' + qs : ''}`,
        token.accessToken
      );
      saveBlob(blob, `merchant_payouts_${payoutStatus}_${datestamp()}.xlsx`);
    } catch (err) {
      setError(err.message);
    } finally {
      setDownloading(false);
    }
  };

  const handleDownloadISOSpreadsheet = async () => {
    try {
      setDownloading(true);
      setError(null);
      const params = new URLSearchParams();
      if (isoStatus !== 'all') params.append('status', isoStatus);
      if (isoStart) params.append('start_date', isoStart);
      if (isoEnd) params.append('end_date', isoEnd);
      const qs = params.toString();
      const blob = await downloadXlsxReport(
        `/api/v1/payouts/spreadsheet/iso/${qs ? '?' + qs : ''}`,
        token.accessToken
      );
      saveBlob(blob, `iso_payouts_${isoStatus}_${datestamp()}.xlsx`);
    } catch (err) {
      setError(err.message);
    } finally {
      setDownloading(false);
    }
  };

  const handleDownloadSettlementReconciliation = async () => {
    try {
      setDownloading(true);
      setError(null);
      const params = new URLSearchParams();
      if (reconStart) params.append('start_date', reconStart);
      if (reconEnd) params.append('end_date', reconEnd);
      const qs = params.toString();
      const blob = await downloadXlsxReport(
        `/api/v1/reports/settlement-reconciliation/export/${qs ? '?' + qs : ''}`,
        token.accessToken
      );
      saveBlob(blob, `settlement_reconciliation_${datestamp()}.xlsx`);
    } catch (err) {
      setError(err.message);
    } finally {
      setDownloading(false);
    }
  };

  const handleDownloadAddressLedger = async () => {
    try {
      setDownloading(true);
      setError(null);
      const params = new URLSearchParams();
      if (ledgerStart) params.append('start_date', ledgerStart);
      if (ledgerEnd) params.append('end_date', ledgerEnd);
      if (ledgerOnlyPaid) params.append('only_paid', 'true');
      const qs = params.toString();
      const blob = await downloadXlsxReport(
        `/api/v1/reports/deposit-addresses/export/${qs ? '?' + qs : ''}`,
        token.accessToken
      );
      saveBlob(blob, `deposit_address_ledger_${datestamp()}.xlsx`);
    } catch (err) {
      setError(err.message);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 3 }}>
        Reports
      </Typography>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>
              Merchant Payout Bank Spreadsheet
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Per-merchant amounts owed with routing/account details, for
              submission to the bank.
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel>Status</InputLabel>
                <Select
                  value={payoutStatus}
                  label="Status"
                  onChange={e => setPayoutStatus(e.target.value)}
                >
                  <MenuItem value="approved">Approved</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="processing">Processing</MenuItem>
                  <MenuItem value="completed">Completed</MenuItem>
                  <MenuItem value="all">All</MenuItem>
                </Select>
              </FormControl>
              <TextField
                size="small"
                label="Start date"
                type="date"
                value={payoutStart}
                onChange={e => setPayoutStart(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                size="small"
                label="End date"
                type="date"
                value={payoutEnd}
                onChange={e => setPayoutEnd(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Box>
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={handleDownloadBankSpreadsheet}
              disabled={downloading}
            >
              Download Spreadsheet
            </Button>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>
              Deposit Address Ledger
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Ledger mapping every crypto deposit address to its merchant and
              payment session.
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
              <TextField
                size="small"
                label="Start date"
                type="date"
                value={ledgerStart}
                onChange={e => setLedgerStart(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                size="small"
                label="End date"
                type="date"
                value={ledgerEnd}
                onChange={e => setLedgerEnd(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={ledgerOnlyPaid}
                    onChange={e => setLedgerOnlyPaid(e.target.checked)}
                  />
                }
                label="Paid sessions only"
              />
            </Box>
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={handleDownloadAddressLedger}
              disabled={downloading}
            >
              Download Ledger
            </Button>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>
              ISO Payout Spreadsheet
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Per-ISO commission amounts owed with routing/account details, for
              submission to the bank.
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel>Status</InputLabel>
                <Select
                  value={isoStatus}
                  label="Status"
                  onChange={e => setIsoStatus(e.target.value)}
                >
                  <MenuItem value="approved">Approved</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="processing">Processing</MenuItem>
                  <MenuItem value="completed">Completed</MenuItem>
                  <MenuItem value="all">All</MenuItem>
                </Select>
              </FormControl>
              <TextField
                size="small"
                label="Start date"
                type="date"
                value={isoStart}
                onChange={e => setIsoStart(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                size="small"
                label="End date"
                type="date"
                value={isoEnd}
                onChange={e => setIsoEnd(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Box>
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={handleDownloadISOSpreadsheet}
              disabled={downloading}
            >
              Download Spreadsheet
            </Button>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>
              Settlement Reconciliation
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Per-session reconciliation of expected vs. received vs. paid-out
              amounts, including fees, refunds, and commission.
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
              <TextField
                size="small"
                label="Start date"
                type="date"
                value={reconStart}
                onChange={e => setReconStart(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                size="small"
                label="End date"
                type="date"
                value={reconEnd}
                onChange={e => setReconEnd(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Box>
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={handleDownloadSettlementReconciliation}
              disabled={downloading}
            >
              Download Reconciliation
            </Button>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}

const mapStateToProps = state => ({
  token: getToken(state),
});

const mapDispatchToProps = {
  setBreadcrumbs,
};

export const ReportsPage = connect(
  mapStateToProps,
  mapDispatchToProps
)(ReportsPageComponent);
